import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrderOut, EventOut } from '@/types/api';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import OrderNotificationProvider from '@/components/organisms/OrderNotificationProvider/OrderNotificationProvider';
import NotificationBell from '@/components/molecules/NotificationBell/NotificationBell';
import { useNotifications } from '@/contexts/NotificationContext';
import './OrderConfirmation.scss';

/**
 * Keeps the order status live: reloads the order when a real-time notification
 * about it arrives, after the socket reconnects, when the page comes back to
 * the foreground, and (as a safety net) every 15s while the socket is down.
 * Must render inside the NotificationProvider that listens for this order.
 */
const OrderLiveUpdates: React.FC<{ orderId: number; isFinal: boolean; onChange: () => void }> = ({
  orderId,
  isFinal,
  onChange,
}) => {
  const { lastOrderUpdate, connected, connectionId } = useNotifications();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const seenUpdateRef = useRef(lastOrderUpdate?.receivedAt);
  useEffect(() => {
    if (!lastOrderUpdate || lastOrderUpdate.receivedAt === seenUpdateRef.current) return;
    seenUpdateRef.current = lastOrderUpdate.receivedAt;
    if (lastOrderUpdate.orderId === orderId) onChangeRef.current();
  }, [lastOrderUpdate, orderId]);

  const seenConnectionRef = useRef(connectionId);
  useEffect(() => {
    if (connectionId === seenConnectionRef.current) return;
    seenConnectionRef.current = connectionId;
    onChangeRef.current();
  }, [connectionId]);

  useEffect(() => {
    if (isFinal) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') onChangeRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    const poll = connected ? undefined : setInterval(() => {
      if (document.visibilityState === 'visible') onChangeRef.current();
    }, 15000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (poll) clearInterval(poll);
    };
  }, [connected, isFinal]);

  return null;
};

const OrderConfirmation: React.FC = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { formatPrice, formatWithTax } = usePriceFormat();
  const { formatDetailedOrderDate } = useDateFormatter();
  const [order, setOrder] = useState<OrderOut | null>(null);
  const [eventInfo, setEventInfo] = useState<EventOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);




  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError(t('orderConfirmation.invalidOrderId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Use appropriate endpoint based on authentication status
        let orderData: OrderOut;
        if (isAuthenticated) {
          console.log('Fetching order using authenticated endpoint for order ID:', orderId);
          orderData = await apiService.getOrder(parseInt(orderId));
        } else {
          console.log('Fetching order using anonymous endpoint for order ID:', orderId);
          orderData = await apiService.getAnonymousOrder(parseInt(orderId));
        }
        
        setOrder(orderData);
      } catch (err: any) {
        console.error('Error fetching order:', err);
        setError(t('orderConfirmation.errorFetchingOrder'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, t, isAuthenticated]);

  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!order) {
        setEventInfo(null);
        return;
      }
      try {
        if (isAuthenticated) {
          const eventData = await apiService.getEvent(order.event_id);
          setEventInfo(eventData);
          return;
        }
        const events = await apiService.getAnonymousEvents();
        const matched = events.find(eventItem => eventItem.id === order.event_id) || null;
        setEventInfo(matched);
      } catch (err) {
        console.warn('Failed to fetch event info for order:', err);
        setEventInfo(null);
      }
    };

    fetchEventInfo();
  }, [isAuthenticated, order]);

  // Silent reload used by real-time updates: no spinner, and a transient
  // network error keeps the order on screen instead of the error page.
  const reloadOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const orderData = isAuthenticated
        ? await apiService.getOrder(parseInt(orderId))
        : await apiService.getAnonymousOrder(parseInt(orderId));
      setOrder(orderData);
    } catch (err) {
      console.warn('Live order refresh failed:', err);
    }
  }, [orderId, isAuthenticated]);

  const refreshOrder = async () => {
    if (!orderId || refreshing) return;
    
    try {
      setRefreshing(true);
      
      // Use appropriate endpoint based on authentication status
      let orderData: OrderOut;
      if (isAuthenticated) {
        orderData = await apiService.getOrder(parseInt(orderId));
      } else {
        orderData = await apiService.getAnonymousOrder(parseInt(orderId));
      }
      
      setOrder(orderData);
    } catch (err: any) {
      console.error('Error refreshing order:', err);
      setError(t('orderConfirmation.errorFetchingOrder'));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container">
        <div className="order-confirmation-page">
          <Card variant="elevated" className="error-card">
            <div className="error-card__content">
              <h2 className="error-card__title">{t('common.error')}</h2>
              <p className="error-card__message">{error || t('orderConfirmation.orderNotFound')}</p>
              <Button 
                variant="primary" 
                onClick={() => navigate('/events')}
              >
                {t('orderConfirmation.backToEvents')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'preparing': return 'primary';
      case 'ready': return 'success';
      case 'completed': return 'success';
      case 'paid': return 'secondary';
      case 'cancelled': return 'error';
      default: return 'secondary';
    }
  };

  const isCancelled = order.status.toLowerCase() === 'cancelled';
  const isCompleted = order.status.toLowerCase() === 'completed';

  return (
    <OrderNotificationProvider orderId={!isAuthenticated && orderId ? parseInt(orderId) : undefined}>
      <OrderLiveUpdates orderId={order.id} isFinal={isCompleted || isCancelled} onChange={reloadOrder} />
      <div className="container">
        {/* Notification Bell for anonymous users - visible on order confirmation page */}
        {!isAuthenticated && orderId && (
          <div className="order-confirmation-page__notification-bell">
            <NotificationBell />
          </div>
        )}
        <div className="order-confirmation-page">

        <div className="order-confirmation-page__content">
          
          {/* Codigo QR - Ocultar si la orden está completada o cancelada */}
          {order.qr_code_image && !isCompleted && !isCancelled && (
            <div className="order-confirmation-page__order-id">
              <span className="order-confirmation-page__order-id-label">
                {t('orderConfirmation.qrCodeTitle')}
              </span>
              <div className="qr-code-card__image">
                <img
                  src={order.qr_code_image}
                  alt={`Order ${order.id} QR`}
                  className="qr-code-image"
                />
              </div>
              <strong>{order.qr_code_short}</strong>
            </div>
          )}

          {/* Order Summary */}
          <Card variant="elevated" className="order-summary-card">
              <div className="qr-code-card__order-info">
                <p className="order-number">
                  {t('orderConfirmation.orderNumber')}: <strong>#{order.id}</strong>
                </p>
                <p className="order-code">
                  {t('orderConfirmation.qrCodeAlt')}: <strong>{order.qr_code_short}</strong>
                </p>
                {eventInfo?.name && (
                  <p className="order-event">
                    <strong>{t('orderConfirmation.event')}:</strong> {eventInfo.name}
                  </p>
                )}
                <p className="order-date">
                  <strong>{t('orderConfirmation.orderDate')}:</strong> {formatDetailedOrderDate(order.created_at)}
                </p>
                <p className="order-status">
                  <strong>{t('orderConfirmation.status')}:</strong> 
                  <span className={`status status--${getStatusColor(order.status)}`}>
                    {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
                  </span>
                  <Button 
                    className="order-refresh-button"
                    variant="outline" 
                    size="xs"
                    onClick={refreshOrder}
                    disabled={refreshing}
                  >
                    <Icon name="actualizar" size={12} alt="Refresh" />
                  </Button>
                </p>
              </div>
            <div className="order-summary-card__header">
              <h2 className="order-summary-card__title">
                {t('orderConfirmation.orderSummary')}
              </h2>
            </div>
            <div className="order-summary-card__items">
              {order.items.map((item, index) => (
                <div key={index} className="order-summary-card__item">
                  <span className="order-summary-card__item-name">
                    {item.drink_name || item.food_name}
                  </span>
                  <span className="order-summary-card__item-details">
                    {t('orderConfirmation.quantity')}: {item.quantity} × {formatWithTax(
                      item.price_at_time,
                      item.combined_tax_rate !== null && item.combined_tax_rate !== undefined
                        ? item.combined_tax_rate
                        : item.tax_rate_at_time
                    )}
                  </span>
                  {(item.country_tax_rate !== null && item.country_tax_rate !== undefined) && (
                    <div className="order-summary-card__item-tax-breakdown">
                      <span className="order-summary-card__tax-info">
                        {t('order.itemTaxRate')}: {item.tax_rate_at_time.toFixed(2)}%
                      </span>
                      <span className="order-summary-card__tax-info">
                        {t('order.countryTaxRate')}: {item.country_tax_rate.toFixed(2)}%
                      </span>
                      {item.combined_tax_rate !== null && item.combined_tax_rate !== undefined && (
                        <span className="order-summary-card__tax-info order-summary-card__tax-info--combined">
                          {t('order.combinedTaxRate')}: {item.combined_tax_rate.toFixed(2)}%
                        </span>
                      )}
                    </div>
                  )}
                  <span className="order-summary-card__item-total">
                    {formatWithTax(
                      item.price_at_time * item.quantity,
                      item.combined_tax_rate !== null && item.combined_tax_rate !== undefined
                        ? item.combined_tax_rate
                        : item.tax_rate_at_time
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className="order-summary-card__totals">
              <div className="order-summary-card__subtotal">
                <span>{t('cart.subtotal')}:</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="order-summary-card__tax">
                <span>{t('cart.tax')}:</span>
                <span>{formatPrice(order.total_tax)}</span>
              </div>
              <div className="order-summary-card__total">
                <span>{t('cart.total')}:</span>
                <span>{formatPrice(order.total_amount)}</span>
              </div>
            </div>
          </Card>

          {/* Instructions - Ocultar si la orden está cancelada */}
          {!isCancelled && (
            <Card variant="outlined" className="instructions-card">
              <div className="instructions-card__content">
                <h3 className="instructions-card__title">
                  {t('orderConfirmation.instructionsTitle')}
                </h3>
                <ol className="instructions-card__list">
                  <li>{t('orderConfirmation.instruction1')}</li>
                  <li>{t('orderConfirmation.instruction2')}</li>
                  <li>{t('orderConfirmation.instruction3')}</li>
                </ol>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="order-confirmation-page__actions">
            
            <Button 
              variant={isCancelled ? "primary" : "outline"}
              size="lg"
              onClick={() => navigate('/events')}
            >
              {isCancelled ? t('orderConfirmation.backToEvents') : t('orderConfirmation.orderMore')}
            </Button>
            {/*<Button 
              variant="primary" 
              size="lg"
              onClick={() => window.print()}
            >
              {t('orderConfirmation.printOrder')}
            </Button>*/}
          </div>
        </div>
      </div>
    </div>
    </OrderNotificationProvider>
  );
};

export default OrderConfirmation;